/* PACKAGES */
import React, { useState } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';

/* ELEMENTS */
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import Slider from '../../hocs/Slider/Slider';
import BackgroundMedia from './BackgroundMedia/BackgroundMedia';
import Button from './Button/Button';
import Embed from './Embed/Embed';
import FileBlock from './File/File';
import Heading from './Heading/Heading';
import Image from './Image/Image';
import List from './List/List';
import Paragraph from './Paragraph/Paragraph';
import Placeholder from './Placeholder/Placeholder';
import Products from './Products/Products';
import Separator from './Separator/Separator';
import Spacer from './Spacer/Spacer';
import Stockists from './Stockists/Stockists';
import Table from './Table/Table';
import Video from './Video/Video';
import VideoEmbed from './VideoEmbed/VideoEmbed';

/* UTILS */
import { unique } from '../../utils/array';
import { percentToColumn } from '../../utils/columns';
import { purifier } from '../../utils/dom';
import { prefix, translate } from '../../utils/locale';
import { deepMerge } from '../../utils/object';
import { titleCase, urlify, camelCase } from '../../utils/string';
import { isRestricted } from '../../utils/restrict';

/* STYLES */
import s from './TemplateParser.scss';

const components = {
	acfPlaceholder: Placeholder,
	acfProducts: Products,
	acfStockists: Stockists,
	coreButton: Button,
	coreEmbed: VideoEmbed,
	coreFile: FileBlock,
	coreHeading: Heading,
	coreHtml: Embed,
	coreImage: Image,
	coreParagraph: Paragraph,
	coreSeparator: Separator,
	coreSpacer: Spacer,
	coreVideo: Video,
	coreTable: Table,
};

const containers = {};

const heightSettings = ({ attrs = {}, setup }) => {
	const extraProps = {};
	const settings = {};

	if ((attrs.fixedHeightValue && setup.breakPoint === 'desktop')
		|| (attrs.fixedHeightValueMd && setup.breakPoint === 'tablet')
		|| (attrs.fixedHeightValueSm && setup.breakPoint === 'mobile')
	) {
		extraProps.className = ['u_fillContainer'];
		extraProps.style = {
			overflow: 'hidden',
		};
		settings.fill = true;
		settings.focalPoint = attrs.focalPoint;
	}

	if (attrs.fixedHeightValue && setup.breakPoint === 'desktop') extraProps.style.height = `${attrs.fixedHeightValue}vh`;
	if (attrs.fixedHeightValueMd && setup.breakPoint === 'tablet') extraProps.style.height = `${attrs.fixedHeightValueMd}vh`;
	if (attrs.fixedHeightValueSm && setup.breakPoint === 'mobile') extraProps.style.height = `${attrs.fixedHeightValueSm}vh`;

	return [extraProps, settings];
};

const generalSettings = ({ customProps, attrs = {}, template }) => {
	const attrsClassName = attrs.className || [];

	const generalProps = {
		className: [
			s.container,
			s[template],
			...attrsClassName.map((cN) => s[`${template}${cN}`]),
		],
		style: {},
	};

	// Colors
	if (attrs.backgroundColor) generalProps.style.backgroundColor = attrs.backgroundColor;
	if (attrs.gradientColor) generalProps.style.backgroundImage = attrs.gradientColor;
	if (attrs.textColor) {
		generalProps.style.color = attrs.textColor;
		generalProps.className.push('u_white');
	}

	// Alignment
	if (attrs.align && attrs.align !== 'full') generalProps.style.textAlign = attrs.align;
	if (attrs?.layout?.justifyContent && attrs.layout.justifyContent !== 'full') generalProps.style.justifyContent = attrs.layout.justifyContent;
	if (attrs.verticalAlignment) generalProps.className[generalProps.className.length] = s[`container__v${titleCase(attrs.verticalAlignment)}`];
	if (attrs.align === 'full') generalProps.className[generalProps.className.length] = s.container__alignFull;
	if (attrs.isStackedOnMobile === 0) generalProps.className[generalProps.className.length] = s.coreColumns__noStack;

	const extraProps = deepMerge(generalProps, customProps);
	extraProps.className = extraProps.className.join(' ');

	return extraProps;
};

const getElement = (attrs, setup) => {
	const extraProps = {};
	let Element = 'div';

	if (attrs.href) {
		const externalLink = /^(https?|mailto|tel)/.test(attrs.href);
		const anchorLink = /^#/.test(attrs.href);

		if (attrs.target || externalLink || anchorLink) {
			Element = 'a';
			extraProps.href = attrs.href;
			extraProps.target = '_blank';
			extraProps.rel = 'noopener noreferrer';
		} else {
			Element = Link;
			extraProps.to = prefix(setup, attrs.href.replace(/\/+$/, ''));
		}
	}

	return [Element, extraProps];
};

const getTemplate = (template, attrs, setup, useComponents = true) => {
	if (isRestricted(setup, attrs)) return null;
	if (components[template]) {
		if (useComponents) return components[template];
		return NoContainer;
	}
	if (containers[template]) return containers[template];

	if (['ccBlocksMeta'].indexOf(template) === -1) console.log('DEBUG: missing template', template, containers, components);
	return null;
};

containers.acfCategory = ({ content = [], setup, inlineCategoryContent }) => (
	<>
		{
			content.map((childSection) => {
				let tpl = Object.keys(childSection)[0];
				const nextContent = childSection[tpl];
				if (tpl === 'acfCategory') tpl = 'acfInlineCategoryContent';
				const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
				if (!ChildElement) return null;
				return <ChildElement template={tpl} key={nextContent.key} setup={setup} inlineCategoryContent={inlineCategoryContent} {...nextContent} />;
			})
		}
	</>
);

containers.ccBlocksAccordion = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const open = content.reduce((acc, c, i) => (c?.ccBlocksAccordionItem?.attrs?.open && acc === -1 ? i : acc), -1);
	const [accordianToggled, toggleAccordian] = useState(open);
	const toggleAccordianHandler = (e) => {
		e.preventDefault();
		const value = e.currentTarget.value;
		toggleAccordian((prevState) => (prevState === parseInt(value, 10) ? -1 : parseInt(value, 10)));
	};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection, i) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} accordianToggled={accordianToggled === i} toggleAccordianHandler={toggleAccordianHandler} index={i} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.ccBlocksAccordionItem = ({ attrs = {}, content = [], setup, template, accordianToggled, toggleAccordianHandler, index, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			<button aria-controls={urlify(attrs.title.slice(0, 15))} aria-expanded={accordianToggled} type="button" value={index} onClick={toggleAccordianHandler} className={[s.container_title, (accordianToggled ? s.container_title__expanded : null)].join(' ')}>
				<svg xmlns="http://www.w3.org/2000/svg" width="14" height="9" viewBox="0 0 14 9">
					<polyline fill="none" stroke="#2B3337" strokeLinecap="square" strokeWidth="1.5" points="132 167.294 138 173.294 144 167.294" transform="translate(-131 -166)" />
				</svg>
				<span>{ attrs.title }</span>
			</button>
			<div aria-hidden={!accordianToggled} id={urlify(attrs.title.slice(0, 15))} className={[s.container_content, (accordianToggled ? s.container_content__expanded : null)].join(' ')}>
				{
					content.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) return null;
						return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
					})
				}
			</div>
		</div>
	);
};

containers.ccBlocksPuff = ({ attrs = {}, content = [], uid, setup, template, ...props }) => {
	const [customProps, settings] = heightSettings({ attrs, setup });

	const [Element, elementProps] = getElement(attrs, setup);
	customProps.className = [s[`container__${camelCase(attrs.layout || 'center Center')}`]];
	if (content.filter((childSection) => Object.keys(childSection)[0] === 'coreHtml')) customProps.className.push(s.ccBlocksPuff__embedInside);
	const extraProps = generalSettings({ customProps: deepMerge(customProps, elementProps), attrs, template });
	if (extraProps?.style?.overflow) {
		if (!extraProps.className) extraProps.className = '';
		extraProps.className += ` ${s.ccBlocksPuff__fixedHeight}`;
	}

	return (
		<Element {...extraProps}>
			<BackgroundMedia content={attrs} uid={uid} {...settings} {...props} showPlay={content.length === 0} />
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} isPuffContent setup={setup} {...nextContent} {...props} />;
				})
			}
		</Element>
	);
};
containers.ccBlocksPuffAlt = containers.ccBlocksPuff;

const scrollRight = (e) => {
	e.preventDefault();
	e.currentTarget.nextSibling.scrollLeft += (window.innerWidth * 0.7) + 5;
};

containers.ccBlocksSlider = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const [customProps] = heightSettings({ attrs, setup });
	const infinite = 1;
	// if (attrs?.hint) infinite = 2;
	const sliderAttrs = {
		transitionType: attrs?.transitionType || 'fade',
		transitionSpeed: attrs?.transitionSpeed || 300,
		infinite,
		hint: attrs?.hint || false,
		autoplay: attrs?.autoplay === false ? false : attrs.transitionDelay || 3000,
		interactionTypes: ['touch'],
		showCounter: false,
		adaption: 'content',
	};
	let Wrapper;

	const extraProps = generalSettings({ customProps, attrs, template });

	if (attrs?.freeFlow && attrs?.align === 'full') {
		Wrapper = ({ children }) => (
			<div className={s.container__ccBlocksSlider_inner}>
				<div className={s.container__ccBlocksSlider_inner_overflow}>
					{ children }
				</div>
			</div>
		);
	} else {
		Wrapper = ({ children }) => (
			<Slider {...sliderAttrs}>
				{ children }
			</Slider>
		);
	}

	return (
		<div {...extraProps}>
			<Wrapper>
				{
					content.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) return null;
						return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
					})
				}
			</Wrapper>
		</div>
	);
};

containers.coreButtons = ({ attrs = {}, content = [], setup, template, isProduct, ...props }) => {
	const customProps = {
		className: ['u_buttons'],
		style: {},
	};
	if (!content) return null;
	const isManyButtons = content.filter((tpl) => !isRestricted(setup, tpl[Object.keys(tpl)[0]].attrs));
	const isProductInPageNav = isProduct && isManyButtons.length > 2;
	if (isProductInPageNav) customProps.className.push(s.coreButtons__productInPageNav);
	if (attrs.padding) {
		customProps.style.padding = attrs.padding.join(' ');
		customProps.style.boxSizing = 'border-box';
	}

	const extraProps = generalSettings({ customProps, attrs, template });

	const scrollToProduct = (e) => {
		e.preventDefault();
		const product = document.getElementById('productTop');
		if (product) product.scrollIntoView({ behavior: 'smooth' });
	};

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} isManyButtons={isManyButtons.length} isProductInPageNav={isProductInPageNav} setup={setup} {...nextContent} {...props} />;
				})
			}
			{ isProductInPageNav && <button type="button" className={s.coreButtons__scrollToProduct} onClick={scrollToProduct}>{ translate(setup, 'buyNow') }</button> }
		</div>
	);
};

containers.coreColumn = ({ attrs = {}, content = [], setup, template, column, ...props }) => {
	const customProps = {
		className: [],
	};

	const [Element, elementProps] = getElement(attrs, setup);

	if (attrs?.width && String(attrs.width).indexOf('px') !== -1) {
		customProps.className = ['u_column', 'u_width__set'];
		customProps.style = { width: attrs.width };
	} else if (column) customProps.className = ['u_column', `u_width__${column}`];
	if (!attrs?.width) customProps.className.push('u_width__grow');

	const imageOnlyInColumn = content && content.filter((section) => ['coreImage', 'coreVideo', 'coreEmbed', 'ccBlocksPuff'].indexOf(Object.keys(section).join('')) === -1).length === 0;
	if (imageOnlyInColumn) customProps.className.push(s.container__imageOnly);

	const extraProps = generalSettings({ customProps: deepMerge(customProps, elementProps), attrs, template });

	return (
		<Element {...extraProps}>
			{
				content && content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement || !column) return null;
					return <ChildElement template={tpl} key={nextContent.key} column={column} setup={setup} {...nextContent} {...props} />;
				})
			}
		</Element>
	);
};

containers.coreColumns = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const initialColumnWidths = percentToColumn(content);
	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection, i) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} column={initialColumnWidths[i]} />;
				})
			}
		</div>
	);
};

containers.coreListItem = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<li {...extraProps}>
			{ attrs?.content?.rawText && <div dangerouslySetInnerHTML={{ __html: purifier(attrs.content.rawText) }} /> }
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</li>
	);
};

containers.coreList = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	if (!Array.isArray(content)) return <List content={content} />;

	const extraProps = generalSettings({ customProps, attrs, template });
	let Wrapper = ({ children }) => (
		<ul {...extraProps}>
			{ children }
		</ul>
	);

	if (attrs?.ordered) {
		Wrapper = ({ children }) => (
			<ol {...extraProps}>
				{ children }
			</ol>
		);
	}

	return (
		<Wrapper>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</Wrapper>
	);
};


containers.coreGroup = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {
		className: [],
	};
	if (attrs?.position?.type === 'sticky') customProps.className = [s.coreGroup__sticky];
	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreMore = () => null;

const NoContainer = ({ attrs = {}, content = [], template, setup, ...props }) => {
	const Template = getTemplate(template, attrs, setup);
	if (!Template) return null;

	let Wrapper = ({ children }) => children;

	// Add wrappers
	if (!props.inline && attrs?.align !== 'full' && template !== 'acfProducts' && template !== 'acfPlaceholder') {
		Wrapper = ({ children }) => (
			<div className={s.container__textPage}>
				<div className={s.container_inner}>{ children }</div>
			</div>
		);
	}

	return (
		<ErrorBoundary message={`CMS TemplateSection: ${template}`} debugData={content}>
			<Wrapper>
				<Template content={content} attrs={attrs} setup={setup} {...props} />
			</Wrapper>
		</ErrorBoundary>
	);
};

const categoryTypes = { 'off-ice': 'o1', 'ice-hockey': 'i2', 'roller-hockey': 'r1' };
const sizeTypes = { '----youth': 'yth', '----junior': 'yth', '----senior': 'sr' };

const createRestrictions = (filterType, filterValue, sizeGroups) => {
	let restrictionType = '';
	if (filterType === 'categories') {
		restrictionType = Object.keys(categoryTypes).reduce((acc, category) => {
			if (filterValue.indexOf(category) !== -1) return categoryTypes[category];
			return acc;
		}, '');
	} else {
		restrictionType = sizeGroups[filterValue];
	}

	return restrictionType;
};

const TemplateParser = ({ sections, setup, filter, sizeConversions, variantClasses = false, skipFirst = false, ...props }) => {
	if (!sections) return null;
	if (!Array.isArray(sections)) sections = [sections];
	const templateContents = [];
	let first = true;
	let classes = [];
	if (variantClasses) {
		classes = Array.isArray(variantClasses) ? variantClasses : [variantClasses];
		classes = [...classes.filter((c) => !!c).map((c) => s[c])];
	}

	if (filter.siteWide) {
		const restrictionTypes = [];
		let hasCategory = false;
		let hasSize = false;
		let currentGroup = '';
		const sizeGroups = {};
		sizeConversions.forEach((size) => {
			if (size.base.indexOf('---') !== -1) currentGroup = sizeTypes[urlify(size.base)];
			else sizeGroups[urlify(size.base)] = currentGroup;
		});

		filter.filteredBy.forEach((filterObj) => {
			const filterType = Object.keys(filterObj)[0];
			const filterValue = filterObj[filterType];
			if (filterType === 'categories') hasCategory = true;
			if (filterType === 'sizes') hasSize = true;
			if (filterValue === true || (Array.isArray(filterValue) && filterValue.length > 0)) {
				if (Array.isArray(filterValue)) {
					filterValue.forEach((fV) => {
						restrictionTypes.push(createRestrictions(filterType, fV, sizeGroups));
					});
				} else {
					restrictionTypes.push(createRestrictions(filterType, filterValue, sizeGroups));
				}
			}
		});

		if (!hasCategory) restrictionTypes.push('o1');
		if (!hasSize) restrictionTypes.push('sr');

		const filterRestriction = unique(restrictionTypes);
		// Remove yth from array if both sr and yth exist
		if (filterRestriction.indexOf('yth') !== -1 && filterRestriction.indexOf('sr') !== -1) filterRestriction.splice(filterRestriction.indexOf('yth'), 1);

		setup = deepMerge(setup, { filterRestriction });
	}

	sections.forEach((section, i) => {
		const template = Object.keys(section)[0];
		if (section[template]) {
			const ChildElement = getTemplate(template, section[template].attrs, setup, false);
			if (ChildElement) {
				const Wrapper = ({ children }) => {
					if (classes.length > 0) return <div className={classes.join(' ')}>{ children }</div>;
					return children;
				};
				const nextContent = section[template];
				templateContents[templateContents.length] = (
					<Wrapper key={nextContent.key}>
						<ChildElement template={template} setup={setup} first={skipFirst ? false : first} last={sections.length - 1 === i} uid={nextContent.key} {...nextContent} {...props} />
					</Wrapper>
				);

				// First section that is visible to customer.
				first = false;
			}
		}
	});

	return templateContents;
};

const mapState = ({ filter, environment }, props) => ({
	filter,
	sizeConversions: environment.sizeConversions,
	...props,
});

export default hot(module)(connect(mapState)(TemplateParser));
